/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

export interface InitialScrollResult {
  targetTop: number;
  targetLeft: number;
  targetTaskIndex: number;
}

export const isTaskCompleted = (t: { completed?: boolean; status?: string }): boolean => {
  if (t.completed) return true;
  const s = (t.status || '').toLowerCase().trim();
  return s === 'completed' || s === 'done' || s === 'erledigt' || s === 'abgeschlossen' || s === 'closed';
};

/**
 * Calculates optimal initial vertical and horizontal scroll positions when entering Gantt view.
 * Solves the issue of jumping to an empty white viewport when preceding tasks are completed.
 */
export function calculateInitialGanttScroll(
  tasks: Array<{ completed?: boolean; status?: string; start?: string; end?: string }>,
  dateToIndexMap: Record<string, number>,
  today: string,
  dayWidth: number = 28,
  rowHeight: number = 48
): InitialScrollResult {
  // 1. Identify first active task with dates (so it has a visible bar)
  let targetIndex = tasks.findIndex(t => !isTaskCompleted(t) && (t.start || t.end));
  
  // Fallback: first active task without dates
  if (targetIndex === -1) {
    targetIndex = tasks.findIndex(t => !isTaskCompleted(t));
  }

  // Vertical positioning: 1 row padding above if possible (for visual context)
  const targetTop = targetIndex > 0 ? Math.max(0, (targetIndex - 1) * rowHeight) : 0;

  // 2. Horizontal positioning
  // Focus on TODAY as the primary horizontal anchor whenever present in timeline
  const todayIndex = dateToIndexMap[today];
  let targetLeft: number = 0;

  if (todayIndex !== undefined) {
    if (targetIndex >= 0 && tasks[targetIndex]) {
      const activeTask = tasks[targetIndex];
      const startStr = activeTask.start || activeTask.end;
      const startIndex = startStr ? dateToIndexMap[startStr] : undefined;
      const endStr = activeTask.end || activeTask.start;
      const endIndex = endStr ? dateToIndexMap[endStr] : undefined;

      // If active task spans across today, anchor slightly before so both bar and today line are in clear view
      if (startIndex !== undefined && endIndex !== undefined && startIndex <= todayIndex && endIndex >= todayIndex) {
        const anchor = Math.max(startIndex, todayIndex - 6);
        targetLeft = Math.max(0, anchor * dayWidth - 120);
      } else {
        targetLeft = Math.max(0, todayIndex * dayWidth - 120);
      }
    } else {
      targetLeft = Math.max(0, todayIndex * dayWidth - 120);
    }
  } else if (targetIndex >= 0 && tasks[targetIndex]) {
    // Fallback: If today is outside the project range (e.g. archive), scroll to active task start
    const activeTask = tasks[targetIndex];
    const startStr = activeTask.start || activeTask.end;
    const startIndex = startStr ? dateToIndexMap[startStr] : undefined;
    if (startIndex !== undefined) {
      targetLeft = Math.max(0, startIndex * dayWidth - 120);
    }
  }

  return { targetTop, targetLeft, targetTaskIndex: targetIndex };
}
